import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import catalog from '@/i18n/generated/marketing-landing-locales.json';
import {
  MARKETING_BRAND,
  MARKETING_COMPOSER,
  MARKETING_CTA,
  MARKETING_FAQ,
  MARKETING_FEATURES,
  MARKETING_FOOTER_GROUPS,
  MARKETING_HERO,
  MARKETING_LOGOS,
  MARKETING_PRIMARY_NAV,
  MARKETING_SHOWCASES,
  MARKETING_SHOWCASE_CONTROLS,
  MARKETING_SOLUTIONS_GROUPS,
} from '@/components/marketing-landing/content';
import {
  MARKETING_ROLE_PAGE,
  getMarketingRolePageContent,
} from '@/components/marketing-landing/role-page-content';
import {
  MARKETING_INDUSTRY_PAGE,
  INDUSTRY_BENEFITS_LABELS,
  getMarketingIndustryPageContent,
} from '@/components/marketing-landing/industry-page-content';
import type { MarketingIndustry } from '@/components/marketing-landing/industry-page-model';
import type { MarketingRole } from '@/components/marketing-landing/role-page-model';

/**
 * Localization for the logged-out landing page.
 *
 * Uses the repo's English-string-as-key convention: the English copy in
 * content.ts is the key, and a missing entry falls back to it. That fallback is
 * what makes hrefs, ids, and brand names safe to walk over — they are simply not
 * in the catalog, so they pass through untouched.
 *
 * Called server-side from the route's `locale` param, so the client components
 * receive already-translated plain objects.
 */
type MarketingLandingCatalog = Partial<
  Record<SupportedLocale, Record<string, string>>
>;

const marketingLandingCatalog = catalog as MarketingLandingCatalog;

function localizeValue(
  value: unknown,
  translations: Record<string, string>
): unknown {
  if (typeof value === 'string') return translations[value] ?? value;
  if (Array.isArray(value)) {
    return value.map((item) => localizeValue(item, translations));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        localizeValue(child, translations),
      ])
    );
  }
  return value;
}

export function localizeMarketingLandingCopy(locale: string) {
  const translations = marketingLandingCatalog[normalizeLocale(locale)];
  const t = <T>(value: T): T =>
    translations ? (localizeValue(value, translations) as T) : value;

  return {
    brand: t(MARKETING_BRAND),
    primaryNav: t(MARKETING_PRIMARY_NAV),
    solutionsGroups: t(MARKETING_SOLUTIONS_GROUPS),
    hero: t(MARKETING_HERO),
    composer: t(MARKETING_COMPOSER),
    logos: t(MARKETING_LOGOS),
    showcases: t(MARKETING_SHOWCASES),
    showcaseControls: t(MARKETING_SHOWCASE_CONTROLS),
    features: t(MARKETING_FEATURES),
    faq: t(MARKETING_FAQ),
    cta: t(MARKETING_CTA),
    footerGroups: t(MARKETING_FOOTER_GROUPS),
  };
}

export type MarketingLandingCopy = ReturnType<
  typeof localizeMarketingLandingCopy
>;

export function localizeRolePage(locale: string, role: MarketingRole) {
  const translations = marketingLandingCatalog[normalizeLocale(locale)];
  const t = <T>(value: T): T =>
    translations ? (localizeValue(value, translations) as T) : value;
  const content = getMarketingRolePageContent(role.slug);

  return {
    ...t(MARKETING_ROLE_PAGE),
    ...t(content),
    faq: t(content.faq ?? MARKETING_FAQ),
    cta: t(content.cta ?? MARKETING_CTA),
    titleLines: [t(MARKETING_ROLE_PAGE.titleLead), t(role.label)] as const,
    whyLabel: t(MARKETING_ROLE_PAGE.whyLabel).replace('{role}', t(role.label)),
    roleLabel: t(role.label),
    composer: {
      ...t(MARKETING_COMPOSER),
      placeholder: t(content.composerPlaceholder),
      examples: t(content.useCases),
    },
  };
}

export type MarketingRolePageCopy = ReturnType<typeof localizeRolePage>;

export function localizeIndustryPage(
  locale: string,
  industry: MarketingIndustry
) {
  const translations = marketingLandingCatalog[normalizeLocale(locale)];
  const t = <T>(value: T): T =>
    translations ? (localizeValue(value, translations) as T) : value;

  const content = getMarketingIndustryPageContent(industry.slug);

  return {
    ...t(MARKETING_INDUSTRY_PAGE),
    ...t(content),
    industryLabel: t(industry.label),
    sections: {
      ...t(MARKETING_INDUSTRY_PAGE.sections),
      benefits: t(INDUSTRY_BENEFITS_LABELS[industry.slug]),
    },
    visual: industry.visual,
  };
}

export type MarketingIndustryPageCopy = ReturnType<typeof localizeIndustryPage>;
